import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Technology Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Technology was one of
 * the twelve still carrying it: a two-paragraph series description under an amber
 * note saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını
 * tasarla"*, the same words the Tourism and Utilities cuts arrived in — with a
 * four-part full-page capture of digitopia.co's Technology Industry Focus page as
 * the reference, plus four files: the report mockup, Figure 1, and two overlapping
 * crops of the cover. So this is the twenty-third designed report page and the
 * eighth focus cut to arrive with **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiTourism.ts`
 * and `content/daimiUtilities.ts` do, and `daimiTechnologyChecks()` feeds them into
 * the same manifest as the rest of the site's amber notes so a production build
 * cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda
 * önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That
 * band is `content/reportClosing.ts`, shared by every report page but three, and it
 * already points at the Digital & AI Maturity Report 2026 — so this page inherits
 * the answer rather than restating it, and the reference capture agrees: its Global
 * Perspective card names the 2026 report too.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These
 * documents are prerendered with no client bundle at all (see `src/prerender.tsx`),
 * so an embedded form script would never run, and the other reports on this site
 * hand the reader the PDF from our own bucket instead. So every Download button
 * here is that PDF, and `DAIMI_TC_DOWNLOAD.check` is what keeps the question of
 * gating open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working.
 */
export const DAIMI_TC_SLUG = 'digital-and-ai-maturity-report-2026-technology-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 */
export const DAIMI_TC_INTRO = {
  body: [
    'Technology companies are expected to lead in maturity, yet the 2026 DAIMI benchmark reveals a more nuanced reality. While many have strong digital models and AI capabilities, maturity is not guaranteed. As organizations scale, increasing complexity and fragmented operating models create execution gaps that limit the conversion of technical capability into consistent outcomes.',
    'To lead in 2026, firms must treat digital and AI as an enterprise operating discipline rather than just a product feature.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply
   * gets a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction, Utilities and Tourism cuts made.
   *
   * **With the same caveat the Tourism cover had, file for file.** It arrived as two
   * captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one cut off at the top through the title's margin. The second is
   * the one held here, because it is the only one that contains the whole design; it
   * is 2000 × 1848 rather than square, so it is missing a sliver of the top margin,
   * and the index card's square crop trims about 4% off each side. `check` asks for
   * the file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-technology-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Technology Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production. The live hero has no cover beside it — the right of the hero is empty but for the ribbon — and the cover is in the hero here because that is what the other designed report pages do; say if the live emptiness was the point. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the sharper one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by four lines that each open with a
 * bolded label — "The Endurance Gap:", "Outward vs. Inward Maturity:", "Data
 * Fragmentation:", "The AI Differentiator:". Split into `label` and `body` here so
 * the label can be set rather than emphasised inside a sentence, which is what a
 * `<strong>` in the middle of a paragraph gives.
 */
export const DAIMI_TC_FINDINGS = {
  heading: 'DAIMI Findings: High Velocity vs. The Scale Challenge',
  lede: 'The technology sector shows a broad spread in performance, characterized by high innovation speed but often lagging foundational endurance.',
  points: [
    {
      label: 'The Endurance Gap',
      body: 'While organizations move fast in product innovation, endurance often fails to rise at the same pace, because it is shaped by governance and scaled operating practices.',
    },
    {
      label: 'Outward vs. Inward Maturity',
      body: 'Maturity is typically strongest in outward-facing digital products, but weakens where internal execution requires cross-functional standardization and platform consolidation.',
    },
    {
      label: 'Data Fragmentation',
      body: 'Rapid growth and acquisitions frequently create fragmented data estates and inconsistent definitions, which reduces trust and slows decision-making.',
    },
    {
      label: 'The AI Differentiator',
      body: 'Leadership is no longer defined by access to AI, but by the ability to operationalize it at scale while maintaining discipline and measurable impact.',
    },
  ],
  /**
   * Figure 1 itself — the vitality scatter supplied with this request, the same
   * shape the Airlines, Airports, Construction and Tourism cuts carry rather than
   * the portrait score range the Utilities cut does. It is 1200 × 1185 on a white
   * ground, so it gets a white card and fills it whole.
   */
  photo: {
    src: '/photo/daimi-technology-vitality-chart.webp',
    alt: 'DAIMI vitality chart for the technology industry: 121 companies plotted on digital velocity against digital endurance, most clustered between 2.0 and 3.0 on both axes',
  } satisfies Photo,
  caption: 'Figure 1: DAIMI Vitality Chart – Technology Industry',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole; its own furniture says “Company Count 121”, which is the company count this page therefore states in the alt text and nowhere else — the live caption carries neither it nor a year. What wants a reading against digitopia.co is the prose: the lede and the four findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_TC_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Technology Industry',
  body: [
    'To lead in 2026, technology executives must address the “chaos of scale”. Success requires moving beyond “AI FOMO” and uncontrolled experimentation toward building enterprise-wide governance and deployment standards. By standardizing platforms and ensuring upskilling is consistent across both technical and business teams, organizations can transform early speed into a compounding, repeatable advantage.',
    'Download the full report to access detailed DAIMI benchmarks and the strategic roadmap to turn technical talent into industrialized performance.',
  ],
  /** The mockup supplied with this request — the technology cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-technology-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Technology Focus',
  } satisfies Photo,
  check:
    'Two things here. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And on the live page this band is text only: the right half of that row is empty, and the report mockup you attached does not appear on the page anywhere. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every
 * report page but three — and the reference capture for this page shows exactly
 * that pair, pointing at the Digital & AI Maturity Report 2026, which is the "2024
 * değil 2026" the request asked for. Nothing to change: the shared band already
 * links to `DAIMI_SLUG`.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_TC_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Technology Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiTourismChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures and its cover. Three of the notes are a
 * transcription nobody has read against the original, and one is a decision about
 * gating that belongs to marketing.
 */
export function daimiTechnologyChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_TC_INTRO.check },
    { section: 'findings', check: DAIMI_TC_FINDINGS.check },
    { section: 'analysis', check: DAIMI_TC_ANALYSIS.check },
    { section: 'download', check: DAIMI_TC_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_TC_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
